import React, { useCallback, useMemo, useRef } from 'react';
import { ModalsKeys, Nullable } from 'global';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { AddCircle, Loop } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { Button, Skeleton, Stack } from '@mui/material';
import { Equipment } from 'modules/desking/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { ListingForm } from 'components/shared/TableListing/types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  collectQuoteFromInvoiceForm,
  collectInvoiceDTOFromQuote,
  mapQuoteEquipmentToSync,
  CalculatedTotalsByLevel,
  Quote,
  QuoteChargeListingEntity,
  QuoteChargeType,
} from 'modules/quote';
import { useInvoice } from 'modules/invoice';
import { dealBuilderApi } from '../../../api';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { StepFooterNav } from '../../components/StepFooterNav';
import { CustomerDepositForm } from './CustomerDepositForm';

export const PricingStep = () => {
  const {
    vo,
    activeEntity,
    selectedEquipmentItem,
    updateQuote,
    updateEquipments,
    updateInvoice,
    calculatedTotals,
    setCalculatedTotals,
  } = useDealBuilderContext();
  const { handleApplyFeesAndTaxes, isApplyingFeesAndTaxes } = useInvoice();
  const { handleOpen, handleClose } = useModal(ModalsKeys.Confirmation);
  const chargesSubmitRef = useRef<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>(null);

  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';
  const dynamicsVOId = vo?.dynamicsVendorOpportunityId ?? '';
  const quote = (activeEntity?.quote ?? null) as unknown as Nullable<Quote>;

  const { data: equipmentQueryData, loading: equipmentLoading } = useQuery<{ equipment: Equipment }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById,
    {
      variables: { id: selectedEquipmentItem?.equipment.id ?? '' },
      skip: !selectedEquipmentItem,
    }
  );

  const handleSubscribeTotals = useCallback(
    (calc: CalculatedTotalsByLevel) => {
      setCalculatedTotals(calc);

      if (!chargesSubmitRef.current) {
        return;
      }

      const formValues = chargesSubmitRef.current?.submit()?.bodyFields ?? [];

      const quoteDTO = collectQuoteFromInvoiceForm(formValues);
      updateQuote({ ...quoteDTO, vendorOpportunityId });

      const equipmentDTOs = formValues
        .filter(({ type }) => type === QuoteChargeType.Equipment)
        .map(({ equipment }) => ({
          ...mapQuoteEquipmentToSync(equipment!),
          vendorOpportunityId,
          equipmentId: equipment!.equipmentId,
          dynamicsVendorOpportunityId: dynamicsVOId,
        }));
      updateEquipments(equipmentDTOs);

      const invoiceDTO = collectInvoiceDTOFromQuote(quoteDTO);
      updateInvoice({ VOId: vendorOpportunityId, entityId: dynamicsVOId, fieldsObj: invoiceDTO });
    },
    [vendorOpportunityId, dynamicsVOId, updateQuote, updateEquipments, updateInvoice, setCalculatedTotals]
  );

  const equipmentList = useMemo<Equipment[]>(() => {
    if (!equipmentQueryData?.equipment) return [];
    return [{ ...equipmentQueryData.equipment, vendorOpportunityId }];
  }, [equipmentQueryData?.equipment, vendorOpportunityId]);

  return (
    <CardContainer
      title="Pricing"
      subTitle="Edit pricing line items, add charges, and review pricing before building a payment structure"
    >
      <RenderWithFallback
        isLoading={equipmentLoading}
        fallBack={<Skeleton variant="rectangular" width="100%" height={300} />}
      >
        <InvoiceDetailsListing
          VOId={vendorOpportunityId}
          quote={quote}
          equipmentList={equipmentList}
          dynamicsVOId={dynamicsVOId}
          chargesSubmitRef={chargesSubmitRef}
          onSubscribeTotals={handleSubscribeTotals}
          actionPanelBottom={({ onAddCharge }) => (
            <Stack direction="row" gap="1rem" sx={{ paddingBottom: '0.5rem', borderBottom: `1px solid ${grey[300]}` }}>
              <Button onClick={onAddCharge} startIcon={<AddCircle />} size="small">
                Add Invoice Item
              </Button>
              <Button
                sx={{ ml: 'auto' }}
                startIcon={<Loop />}
                loading={isApplyingFeesAndTaxes}
                onClick={() => {
                  handleOpen({
                    title: 'Confirmation',
                    message:
                      'Warning - all existing taxes, fees, charges, and discounts on this invoice and associated equipment will be removed and replaced. Continue?',
                    onConfirm: async () => {
                      handleClose();
                      await handleApplyFeesAndTaxes(vendorOpportunityId);
                    },
                  });
                }}
              >
                Reapply Taxes and Fees
              </Button>
            </Stack>
          )}
        />

        <CustomerDepositForm />

        <NetFinanceAmountListing {...calculatedTotals} />
      </RenderWithFallback>

      <StepFooterNav />
    </CardContainer>
  );
};
